import React, { useState, useEffect } from 'react';
import { Search, LogOut, Mail, Phone, Calendar, Target, MessageSquare, CheckCircle, Clock, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ onLogout }) => {
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  useEffect(() => {
    filterSubmissions();
  }, [submissions, searchTerm, statusFilter]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contact/`);
      setSubmissions(response.data.submissions || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast({
        title: "Error",
        description: "Failed to load submissions. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filterSubmissions = () => {
    let filtered = [...submissions];

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => sub.status === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(sub =>
        sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (sub.message && sub.message.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredSubmissions(filtered);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { label: 'New', className: 'bg-blue-500 hover:bg-blue-600', icon: Clock },
      contacted: { label: 'Contacted', className: 'bg-yellow-500 hover:bg-yellow-600', icon: MessageSquare },
      converted: { label: 'Converted', className: 'bg-green-500 hover:bg-green-600', icon: CheckCircle }
    };

    const config = statusConfig[status] || statusConfig.new;
    const Icon = config.icon;

    return (
      <Badge className={`${config.className} text-white flex items-center gap-1`}>
        <Icon size={14} />
        {config.label}
      </Badge>
    );
  };

  const stats = {
    total: submissions.length,
    new: submissions.filter(s => s.status === 'new').length,
    contacted: submissions.filter(s => s.status === 'contacted').length,
    converted: submissions.filter(s => s.status === 'converted').length
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-brand-black text-white py-6 px-4 shadow-lg">
        <div className="container mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-gray-300 mt-1">The WillisTRAINING Method™</p>
            </div>
            <Button
              onClick={onLogout}
              className="bg-white/10 hover:bg-white/20 text-white border-white/20"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white shadow-lg">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Total Leads</p>
                  <p className="text-3xl font-bold text-brand-black mt-2">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-brand-orange/10 rounded-full flex items-center justify-center">
                  <Mail className="text-brand-orange" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm font-medium">New</p>
                  <p className="text-3xl font-bold text-blue-600 mt-2">{stats.new}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Clock className="text-blue-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Contacted</p>
                  <p className="text-3xl font-bold text-yellow-600 mt-2">{stats.contacted}</p>
                </div>
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <MessageSquare className="text-yellow-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-lg">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-gray-500 text-sm font-medium">Converted</p>
                  <p className="text-3xl font-bold text-green-600 mt-2">{stats.converted}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="text-green-600" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-8 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  type="text"
                  placeholder="Search by name, email, or message..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setStatusFilter('all')}
                  variant={statusFilter === 'all' ? 'default' : 'outline'}
                  className={statusFilter === 'all' ? 'bg-brand-black' : ''}
                >
                  All
                </Button>
                <Button
                  onClick={() => setStatusFilter('new')}
                  variant={statusFilter === 'new' ? 'default' : 'outline'}
                  className={statusFilter === 'new' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                >
                  New
                </Button>
                <Button
                  onClick={() => setStatusFilter('contacted')}
                  variant={statusFilter === 'contacted' ? 'default' : 'outline'}
                  className={statusFilter === 'contacted' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
                >
                  Contacted
                </Button>
                <Button
                  onClick={() => setStatusFilter('converted')}
                  variant={statusFilter === 'converted' ? 'default' : 'outline'}
                  className={statusFilter === 'converted' ? 'bg-green-600 hover:bg-green-700' : ''}
                >
                  Converted
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submissions List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Loading submissions...</p>
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-12 text-center">
              <XCircle className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-500 text-lg">No submissions found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {filteredSubmissions.map((submission) => (
              <Card key={submission.id} className="shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-xl font-bold text-brand-black">{submission.name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            {getStatusBadge(submission.status)}
                            <span className="text-sm text-gray-500">
                              <Calendar size={14} className="inline mr-1" />
                              {formatDate(submission.submitted_at)}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 text-gray-700">
                          <Mail size={16} className="text-brand-orange" />
                          <a href={`mailto:${submission.email}`} className="hover:text-brand-orange">
                            {submission.email}
                          </a>
                        </div>
                        {submission.phone && (
                          <div className="flex items-center gap-2 text-gray-700">
                            <Phone size={16} className="text-brand-orange" />
                            <a href={`tel:${submission.phone}`} className="hover:text-brand-orange">
                              {submission.phone}
                            </a>
                          </div>
                        )}
                      </div>

                      {submission.goal && (
                        <div className="flex items-start gap-2 text-gray-700">
                          <Target size={16} className="text-brand-orange mt-1" />
                          <div>
                            <span className="font-semibold text-sm">Training Goal:</span>
                            <p className="text-sm">{submission.goal}</p>
                          </div>
                        </div>
                      )}

                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="font-semibold text-sm text-gray-700 mb-2">Message:</p>
                        <p className="text-gray-700 leading-relaxed">{submission.message}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;